/**
 * Family Service
 * Handles API calls for family management
 */
import { apiGet, apiPost, apiPut, apiDelete } from './api';
import { PaginatedResponse } from '../types/api';

export interface FamilyMember {
  familyMemberID?: number;
  individualID: number;
  clientID: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender?: string;
  phoneNumber?: string;
  emailAddress?: string;
  relationshipType: string;
  isPrimaryContact: boolean;
}

export interface Family {
  familyID?: number;
  familyName: string;
  primaryContactID: number;
  primaryContactName?: string;
  primaryContactClientID?: string;
  memberCount?: number;
  members?: FamilyMember[];
  createdAt?: string;
  updatedAt?: string;
}

export interface FamilyFilters {
  searchQuery?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Get families with optional search and pagination
 */
export async function getAllFamilies(filters?: FamilyFilters): Promise<PaginatedResponse<Family>> {
  const response = await apiGet<Family[]>('/families', filters);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load families. Please try again.');
  }

  return response as PaginatedResponse<Family>;
}

/**
 * Get family by ID with all members
 */
export async function getFamilyById(familyID: number): Promise<Family> {
  const response = await apiGet<Family>(`/families/${familyID}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load family details. Please try again.');
  }

  return response.data;
}

/**
 * Create new family
 */
export async function createFamily(family: {
  familyName: string;
  primaryContactID: number;
  primaryContactRelationship?: string;
}): Promise<Family> {
  const response = await apiPost<Family>('/families', family);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to create family. Please try again.');
  }

  return response.data;
}

/**
 * Update family
 */
export async function updateFamily(
  familyID: number,
  family: {
    familyName: string;
    primaryContactID: number;
  }
): Promise<Family> {
  const response = await apiPut<Family>(`/families/${familyID}`, family);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update family. Please try again.');
  }

  return response.data;
}

/**
 * Delete family
 */
export async function deleteFamily(familyID: number): Promise<void> {
  const response = await apiDelete<void>(`/families/${familyID}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to delete family. Please try again.');
  }
}

/**
 * Add member to family
 */
export async function addFamilyMember(
  familyID: number,
  member: {
    individualID: number;
    relationshipType: string;
  }
): Promise<void> {
  const response = await apiPost<void>(`/families/${familyID}/members`, member);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to add family member. Please try again.');
  }
}

/**
 * Remove member from family
 */
export async function removeFamilyMember(familyID: number, memberID: number): Promise<void> {
  const response = await apiDelete<void>(`/families/${familyID}/members/${memberID}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to remove family member. Please try again.');
  }
}

// Legacy export for backward compatibility
export const familyService = {
  getAllFamilies,
  getFamilyById,
  createFamily,
  updateFamily,
  deleteFamily,
  addFamilyMember,
  removeFamilyMember,
};
